//Svelte
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
//Types
import type { VirtualComponentConfig } from 'src/types/VirtualComponents';
//Helpers
import { getVCIcon, VC_BUTTON_PROPS } from './virtual_component_utils';

const _number_operators = ['less than', 'more than', 'equal'];
const _format = get(format);

//@ToDo: Further extract the used types
type VCConditionHandler = (
	component_type: string,
	component_id: number,
	component_settings: VirtualComponentConfig,
) => any;

const _VC_CONDITION_NUMBER: VCConditionHandler = (
	component_type: string,
	component_id: number,
	component_settings: VirtualComponentConfig,
) => {
	const icon = getVCIcon(component_settings) || 'fa fa-calculator';
	const title = component_settings?.name || component_type + ' ' + component_id;
	return {
		title, //Name
		props: {
			id: component_type, //VC type
			values: _number_operators,
			type: 'virtual_component',
			input_value: component_settings?.default_value,
			input_index: component_id, //VC id
			character: component_settings?.meta?.ui?.unit || '', //VC settings (component:id -> meta -> ui -> unit)
			icon,
			i18tr: title,
			config: {
				min: component_settings?.min,
				max: component_settings?.max,
			},
		},
	};
};

const _VC_CONDITION_BOOLEAN: VCConditionHandler = (
	component_type: string,
	component_id: number,
	component_settings: VirtualComponentConfig,
) => {
	const titles = component_settings?.meta?.ui?.titles || [];
	const kv_options = [
		{
			key: 'true',
			value: titles[1] || 'True',
		},
		{
			key: 'false',
			value: titles[0] || 'False',
		},
	];

	return {
		title: component_settings?.name || component_type + ' ' + component_id, //Name
		props: {
			id: component_type, //VC type
			key_values: kv_options, // Options to choose from
			type: 'virtual_component',
			input_index: component_id, //VC id
			i18tr: 'boolean',
		},
	};
};

const _VC_CONDITION_BUTTON: VCConditionHandler = (
	component_type: string,
	component_id: number,
	component_settings: VirtualComponentConfig,
) => {
	const kv_options = Object.keys(VC_BUTTON_PROPS).map((name) => ({
		key: name,
		icon: VC_BUTTON_PROPS[name].icon,
		value: _format('condition_operator_push_type_' + VC_BUTTON_PROPS[name].operator),
	}));

	return {
		title: component_settings?.name || component_type + ' ' + component_id, //Name
		props: {
			id: component_type, // VC type
			subtitle: _format('title_scene_condition_push_type'), //Override subtitle
			key_values: kv_options, // Options to choose from
			type: 'virtual_component',
			input_index: component_id, // VC id
		},
	};
};

const _VC_CONDITION_ENUM: VCConditionHandler = (
	component_type: string,
	component_id: number,
	component_settings: VirtualComponentConfig,
) => {
	const enum_titles = component_settings?.meta?.ui?.titles || [];
	const enum_kv =
		component_settings?.options.map((name) => ({
			key: name,
			value: enum_titles?.[name] || name,
		})) || [];

	return {
		title: component_settings?.name || component_type + ' ' + component_id, //Name
		props: {
			id: component_type, // VC type
			key_values: enum_kv, // Options to choose from
			type: 'virtual_component',
			input_index: component_id, //VC id
			i18tr: 'enum',
		},
	};
};

const _VC_CONDITION_TEXT: VCConditionHandler = (
	component_type: string,
	component_id: number,
	component_settings: VirtualComponentConfig,
) => {
	const icon = getVCIcon(component_settings) || 'fa fa-font';
	return {
		title: component_settings?.name || component_type + ' ' + component_id, //Name
		props: {
			id: component_type, // VC type
			type: 'virtual_component',
			icon,
			input_text: component_settings?.default_value,
			config: {
				max_length: component_settings?.max_len || 255,
			},
			input_index: component_id, //VC id
			i18tr: 'text',
		},
	};
};

/**
 * Get the handler function for the VC condition by component TYPE
 * @param {string} component_type
 * @returns {VCConditionHandler | undefined}  Function handler for the VC condition or undefined if not found
 *
 */
export const _get_vc_condition_handler = (component_type: string): VCConditionHandler | undefined => {
	let handler: VCConditionHandler = undefined;
	switch (component_type) {
		case 'button':
			handler = _VC_CONDITION_BUTTON;

			break;
		case 'number':
			handler = _VC_CONDITION_NUMBER;

			break;
		case 'boolean':
			handler = _VC_CONDITION_BOOLEAN;

			break;
		case 'enum':
			handler = _VC_CONDITION_ENUM;

			break;
		case 'text':
			handler = _VC_CONDITION_TEXT;

			break;
		default:
			break;
	}
	return handler;
};

export const init_virtual_device_conditions = (device, status, settings) => {
	const channel = device?.channel;
	const group_components = status?.['group:' + channel]?.value || [];

	if (import.meta.env.DEV) {
		console.log('INIT virtual device conditions for ', device.id, channel);
		console.log('- Virtual components: ', group_components);
	}

	let vc_conditions = [];
	group_components.forEach((component: string) => {
		let [component_type, component_id] = component.split(':');
		const component_settings = settings?.[component];

		let handler = _get_vc_condition_handler(component_type);

		if (handler)
			vc_conditions.push(handler(component_type, Number(component_id), component_settings));
	});
	return vc_conditions;
};
